import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  ICustomerUser,
  ILabReqDTO,
  ILabReq,
  IAdminTokenPayload,
  LabReqTemplate
} from '@care-portals/api-interfaces';
import { getLabReqFax, getLicenseForProvince } from '@care-portals/shared';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthFacade } from '../../../+store/auth/auth.facade';
import { LabReqFacade } from '../../../+store/lab-req/lab-req.facade';
import {
  AB_TRT_DEFAULT_VALUE,
  AB_LGV_DEFAULT_VALUE,
  AB_WL_DEFAULT_VALUE,
  BC_TRT_DEFAULT_VALUE,
  BC_LGV_DEFAULT_VALUE,
  BC_LGV_PAID_DEFAULT_VALUE,
  BC_WL_DEFAULT_VALUE,
  LabReqBaseComponent,
  NF_LGV_DEFAULT_VALUE,
  NF_TRT_DEFAULT_VALUE,
  ON_LGV_PAID_DEFAULT_VALUE,
  QC_LGV_DEFAULT_VALUE,
  QC_TRT_DEFAULT_VALUE,
  QC_WL_DEFAULT_VALUE,
  SK_LGV_DEFAULT_VALUE,
  SK_LGV_PAID_DEFAULT_VALUE,
  SK_TRT_DEFAULT_VALUE,
  SK_WL_DEFAULT_VALUE
} from '@care-portals/ui';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { AppFacade } from '../../../+store/app/app.facade';
import { addDays, addMonths, format } from 'date-fns';

@Component({
  selector: 'my-health-admin-lab-requisition-detail-v2',
  templateUrl: './lab-requisition-detail-v2.component.html',
  styleUrls: ['./lab-requisition-detail-v2.component.less']
})
export class LabRequisitionDetailV2Component
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('base')
  labReqBase: LabReqBaseComponent;

  labReq: ILabReq;
  @Input() template: LabReqTemplate;
  @Input() mode: 'edit' | 'create';
  customer: ICustomerUser;
  user: IAdminTokenPayload;
  saving: boolean;
  isStart = true;
  stop$ = new Subject();
  showAlert = false;
  dueDate: Date;
  organization: string;

  constructor(
    private facade: LabReqFacade,
    public authFacade: AuthFacade,
    private nzDrawerRef: NzDrawerRef,
    private customerFacade: CustomerFacade,
    private appFacade: AppFacade
  ) {
    this.facade.saving$.pipe(takeUntil(this.stop$)).subscribe(res => {
      this.saving = res;
      if (!res && !this.isStart) {
        this.nzDrawerRef.close(true);
      }
    });
  }

  ngOnInit(): void {
    this.isStart = false;
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.authFacade.user$,
      this.customerFacade.selectedCustomer$,
      this.facade.selectedLabReq$,
      this.appFacade.selectedLayout$
    ])
      .pipe(takeUntil(this.stop$))
      .subscribe(([user, customer, labReq, layout]) => {
        if (!user || !customer || !layout) return;
        const labReqFax = getLabReqFax(layout.organization);
        const providerCredential = user.credential || 'NP';
        if (this.mode === 'edit' && !labReq) return;

        this.user = user;
        this.customer = customer;
        this.labReq = labReq;
        this.organization = layout.organization;
        // sign the lab req if is create
        if (!this.labReq) {
          this.labReqBase.sign(this.user._id);
          this.dueDate = addDays(new Date(), 30);
        }

        if (this.mode === 'edit') {
          this.template = { id: this.labReq.templateId, variant: this.labReq.templateVariant } as any;
          setTimeout(() => {
            let data = this.labReq.data;
            // lgvon stores other tests as flat keys (otherTests_test0, otherTests_test1, ...)
            // v2 form expects nested FormGroup (otherTests_test: { t1, t2, ... })
            if (this.template.id === 'lgvon' && data) {
              data = { ...data } as any;
              const existingTests = (data as any).otherTests_test;
              if (existingTests && typeof existingTests === 'object') {
                const nested: Record<string, string> = {};
                let i = 0;
                while (existingTests[`otherTests_test${i}`] !== undefined) {
                  nested[`t${i + 1}`] = String(existingTests[`otherTests_test${i}`]);
                  i++;
                }
                if (i > 0) (data as any).otherTests_test = nested;
              }
            }
            this.labReqBase.patchValue(data);
            this.dueDate = this.labReq.dueDate
              ? this.labReq.dueDate
              : addDays(new Date(), 30);
          });
        } else {
          // patch default value

          /* ----------------------------------- BC ----------------------------------- */
          if (this.template.id === 'bc') {
            if (this.template.variant === 'trt') {
              this.labReqBase.patchValue(
                BC_TRT_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer,
                  fax: labReqFax
                })
              );
            } else if (this.template.variant === 'wl') {
              this.labReqBase.patchValue(
                BC_WL_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer,
                  fax: labReqFax
                })
              );
            } else if (this.template.variant === 'lgv') {
              this.labReqBase.patchValue(
                BC_LGV_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer,
                  organization: this.organization
                })
              );
            } else if (this.template.variant === 'lgv_paid') {
              this.labReqBase.patchValue(
                BC_LGV_PAID_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer,
                  organization: this.organization
                })
              );
            }
            const patient = this.customer;
            const dob = patient.dob?.split('-') || ['', '', ''];
            const bcLicense = getLicenseForProvince(user.licenses, 'BC');
            const bcPractitionerNumber =
              bcLicense?.msp ?? bcLicense?.number ?? '';
            this.labReqBase.patchValue({
              surname: patient.lastName,
              first: patient.firstName,
              initials:
                (patient.firstName?.[0] ?? '').toUpperCase() +
                (patient.lastName?.[0] ?? '').toUpperCase(),
              dob: {
                day: dob[2],
                month: dob[1],
                year: dob[0]
              },
              billing: {
                PHN: patient?.identifiers?.[0]?.hcNum || ''
              },
              address: {
                patientAddress: patient.defaultAddress
                  ? patient.defaultAddress.address1 +
                    '\n' +
                    patient.defaultAddress.address2
                  : '',
                cityProvince: [
                  patient.defaultAddress?.city,
                  patient.defaultAddress?.provinceCode,
                  patient.defaultAddress?.countryCode === 'CA' ? 'Canada' :
                  patient.defaultAddress?.countryCode === 'US' ? 'USA' :
                  patient.defaultAddress?.country ?? ''
                ].filter(Boolean).join(', '),
                postalCode: patient.defaultAddress?.postalCode
              },
              patientTel: patient.phone,
              date: format(new Date(), 'yyyy/MM/dd'),
              practitionerNumber: `${user.firstName} ${user.lastName} , ${
                user.credential || 'NP'
              } (${bcPractitionerNumber}).\n2620 Bristol Circle, Unit 400,\nOakville, ON L6H 6Z7`
            });
          }

          /* ----------------------------------- AB ----------------------------------- */
          if (this.template.id === 'ab') {
            if (this.template.variant === 'trt') {
              this.labReqBase.patchValue(AB_TRT_DEFAULT_VALUE);
            } else if (this.template.variant === 'wl') {
              this.labReqBase.patchValue(AB_WL_DEFAULT_VALUE);
            } else if (this.template.variant === 'lgv') {
              this.labReqBase.patchValue(
                AB_LGV_DEFAULT_VALUE({
                  customer: this.customer,
                  organization: this.organization
                })
              );
            }
            const patient = this.customer;
            const provider = this.user;
            const providerLicense = getLicenseForProvince(
              provider.licenses,
              'AB'
            );
            const dob = patient.dob?.split('-') || ['', '', ''];
            this.labReqBase.patchValue({
              patient: {
                PHN: patient?.identifiers?.[0]?.hcNum || '',
                exp: patient?.identifiers?.[0]?.hcExp || '',
                dob: `${dob[2]}-${dob[1]}-${dob[0]}`,
                lastName: patient.lastName,
                firstName: patient.firstName,
                phone: patient.phone,
                address: `${patient.defaultAddress?.address1} - ${patient.defaultAddress?.address2}`,
                city: patient.defaultAddress?.city,
                prov:
                  patient.defaultAddress?.provinceCode ||
                  patient.defaultAddress?.province,
                postal: patient.defaultAddress?.postalCode
              },
              provider: {
                name: `${provider.firstName} ${
                  provider.lastName
                } ${providerCredential} [${providerLicense?.label || ''} ${
                  providerLicense?.number || ''
                }]`,
                ccProviderId: providerLicense?.abId || '',
                ccSubmitterId: providerLicense?.submitterId || '',
                address: '2620 Bristol Circle, Unit 400, Oakville, ON L6H 6Z7',
                phone: '289-401-4678',
                clinicName: `Jack & Jill Health  Fax ${labReqFax}`
              }
            });
          }

          /* ----------------------------------- qc ----------------------------------- */
          if (this.template.id === 'qc') {
            const patient = this.customer;
            const provider = this.user;
            const providerLicense = getLicenseForProvince(
              provider.licenses,
              'QC'
            );
            const dob = patient.dob?.split?.('-');

            if (this.template.variant === 'trt') {
              this.labReqBase.patchValue(
                QC_TRT_DEFAULT_VALUE({ user: provider, customer: patient })
              );
            } else if (this.template.variant === 'wl') {
              this.labReqBase.patchValue(
                QC_WL_DEFAULT_VALUE({ user: provider, customer: patient })
              );
            } else if (this.template.variant === 'lgv') {
              this.labReqBase.patchValue(
                QC_LGV_DEFAULT_VALUE({
                  user: provider,
                  customer: patient,
                  organization: this.organization
                })
              );
            }

            this.labReqBase.patchValue({
              patient: {
                name: patient?.lastName,
                firstName: patient?.firstName,
                dob: dob ? `${dob[0]}/${dob[1]}/${dob[2]}` : '',
                address: `${patient?.defaultAddress?.address1} - ${patient?.defaultAddress?.address2}`,
                city: patient?.defaultAddress?.city,
                postalCode: patient?.defaultAddress?.postalCode,
                mobileT: patient?.phone,
                healthInsurance: patient?.identifiers?.[0]?.hcNum || ''
              },
              collectionAgencyInfo: {
                signatureDate: format(new Date(), 'yyyy/MM/dd'),
                doctor: `${provider.firstName} ${
                  provider.lastName
                } ${providerCredential} [${providerLicense?.label || ''} ${
                  providerLicense?.number || ''
                }]`,
                permitNumber: providerLicense?.qc,
                clinic: 'Jack & Jill Health (Cover Health)',
                address: '2620 Bristol Circle, Unit 400, Oakville, ON L6H 6Z7',
                T: '1-905-291-7970',
                F: labReqFax
              }
            });
          }
          /* ----------------------------------- sk ----------------------------------- */
          if (this.template.id === 'sk') {
            const patient = this.customer;
            const provider = this.user;
            const providerLicense = getLicenseForProvince(
              provider.licenses,
              'SK'
            );
            const dob = patient.dob?.split?.('-');
            const normalizedGender = patient?.gender?.toLowerCase?.();

            if (this.template.variant === 'trt') {
              this.labReqBase.patchValue(SK_TRT_DEFAULT_VALUE());
            } else if (this.template.variant === 'wl') {
              this.labReqBase.patchValue(SK_WL_DEFAULT_VALUE());
            } else if (this.template.variant === 'lgv') {
              this.labReqBase.patchValue(
                SK_LGV_DEFAULT_VALUE({
                  customer: patient,
                  organization: this.organization
                })
              );
            } else if (this.template.variant === 'lgv_paid') {
              this.labReqBase.patchValue(
                SK_LGV_PAID_DEFAULT_VALUE({
                  customer: patient,
                  organization: this.organization
                })
              );
            }

            this.labReqBase.patchValue({
              male: normalizedGender === 'male' || normalizedGender === 'm',
              female: normalizedGender === 'female' || normalizedGender === 'f',
              additionalCopiesOfReportTo: `fax results to ${labReqFax}`,
              clinicNameOrBillingInfo: `billing #${
                providerLicense?.skBilling || ''
              }   Jack & Jill Health (Cover Health) #997`,
              firstName: patient?.firstName,
              lastName: patient?.lastName,
              DOB: dob ? `${dob[2]}/${dob[1]}/${dob[0]}` : '',
              phone: patient?.phone,
              requestingPhysicianFirstName: provider.firstName,
              requestingPhysicianLastName: `${
                provider.lastName
              } ${providerCredential} [${providerLicense?.label || ''} ${
                providerLicense?.number || ''
              }]`
            });
          }

          /* ----------------------------------- on ----------------------------------- */
          if (this.template.id === 'on' || this.template.id === 'lgvon') {
            const patient = this.customer;
            const provider = this.user;
            const providerLicense = getLicenseForProvince(provider.licenses, 'ON');
            const dob = patient.dob?.split?.('-');

            if (this.template.variant === 'lgv_paid') {
              this.labReqBase.patchValue(
                ON_LGV_PAID_DEFAULT_VALUE({
                  customer: patient,
                  organization: this.organization
                })
              );
            }

            if (this.template.id === 'lgvon') {
              const gender = patient?.gender?.toLowerCase?.();
              const isMale = gender === 'male' || gender === 'm';
              const jackTests = [
                'Testosterone free and total', 'B12', 'Estradiol', 'LH', 'FSH', 'TSH',
                'Prolactin', 'Lipase', 'CRP', 'Total Insulin', 'Magnesium (Serum)',
                'Lipoprotein(a)', 'Homocysteine', 'AST', 'SHBG', 'Cortisol (random)',
                'Serum iron (Fe)', 'TIBC', '% transferrin saturation', 'Ferritin',
                'Free T3/Free T4', 'GGT (Gamma-glutamyl transferase)', 'Calcium',
                'Serum Phosphate (PO4)', 'Nucleated RBC (NRBC)'
              ];
              const jillTests = [
                'B12', 'Serum iron (Fe)', 'TIBC', '% transferrin saturation', 'Ferritin',
                'TSH', 'Lipase', 'CRP', 'Total Insulin', 'Progesterone', 'Estradiol',
                'LH', 'FSH', 'Testosterone (Total)', 'Prolactin', 'DHEA-S',
                'Free T3/Free T4', 'Magnesium (Serum)', 'AST',
                'GGT (Gamma-glutamyl transferase)', 'Lipoprotein(a)', 'Cortisol (random)',
                'Calcium', 'Serum Phosphate (PO4)', 'Nucleated RBC (NRBC)',
                'Apolipoprotein B (ApoB)'
              ];
              const tests = isMale ? jackTests : jillTests;
              const otherTests: Record<string, string> = {};
              tests.forEach((t, i) => { otherTests[`t${i + 1}`] = t; });
              this.labReqBase.patchValue({
                biochemistry_glucose: true,
                biochemistry_glucose_glucose_fasting: true,
                biochemistry_hbA1C: true,
                biochemistry_creatinine: true,
                biochemistry_uricAcid: isMale,
                biochemistry_sodium: true,
                biochemistry_potassium: true,
                biochemistry_alt: true,
                biochemistry_alk: true,
                biochemistry_albumin: true,
                biochemistry_lipid: true,
                hematology_cbc: true,
                insuranceType_OHIP: true,
                psa_insured: isMale,
                otherTests_test: otherTests,
              });
            }

            this.labReqBase.patchValue({
              urgentContactNumber_contactNumber: '905-291-7970',
              serviceDate: format(new Date(), 'yyyy-MM-dd'),
              clinicalInformation: '',
              adminUser: {
                name: `${provider.firstName} ${provider.lastName}`,
                credential: provider.credential || 'NP',
                selectedLicense: {
                  onId: providerLicense?.label || '',
                  number: providerLicense?.number || ''
                },
                fax: labReqFax
              },
              user: {
                identifiers: patient?.identifiers || [],
                dob: dob ? `${dob[0]}-${dob[1]}-${dob[2]}` : '',
                gender: patient?.gender,
                defaultAddress: patient?.defaultAddress || {},
                phone: patient?.phone,
                lastName: patient?.lastName,
                firstName: patient?.firstName
              }
            });
          }

          /* ----------------------------------- nf ----------------------------------- */
          if (this.template.id === 'nf') {
            const patient = this.customer;
            const provider = this.user;
            const dob = patient.dob?.split?.('-');
            const providerLicense = getLicenseForProvince(
              provider.licenses,
              'NF'
            );

            if (this.template.variant === 'lgv') {
              this.labReqBase.patchValue(
                NF_LGV_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer,
                  organization: this.organization
                })
              );
            } else {
              this.labReqBase.patchValue(
                NF_TRT_DEFAULT_VALUE({
                  user: this.user,
                  customer: this.customer
                })
              );
            }

            this.labReqBase.patchValue({
              patient: {
                name: ` ${patient?.firstName} ${patient?.lastName}`,
                dob: dob ? `${dob[0]}/${dob[1]}/${dob[2]}` : '',
                sex:
                  patient?.gender === 'male'
                    ? 'M'
                    : patient?.gender === 'female'
                    ? 'F'
                    : 'UN',
                address: `${patient?.defaultAddress?.address1} - ${patient?.defaultAddress?.address2}`,
                city: patient?.defaultAddress?.city,
                postalCode: patient?.defaultAddress?.postalCode,
                homeT: patient?.phone,
                homeTcheck: true,
                hcn: patient?.identifiers?.[0]?.hcNum || '',
                expiry: patient?.identifiers?.[0]?.hcExp || ''
              },
              provider: {
                date: format(new Date(), 'yyyy MMM dd'),
                name: `${user.firstName} ${
                  user.lastName
                } ${providerCredential} [${providerLicense?.label || ''} ${
                  providerLicense?.number || ''
                }]`,
                clinicName: `Jack & Jill Health`,
                address: 'Unit 400-2620 Bristol Circle',
                city: 'Oakville',
                province: 'ON',
                postalCode: 'L6H 6Z7',
                phone: '289-401-4678',
                fax: labReqFax
              }
            });
          }
        }
      });
  }
  ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.complete();
  }

  setDueDate(months: number): void {
    this.dueDate = addMonths(new Date(), months);
  }

  saveLabReq() {
    this.labReqBase.markAllAsTouched();

    if (!this.labReqBase.isValid) {
      this.showAlert = true;
      setTimeout(() => {
        this.showAlert = false;
      }, 5000);
      return;
    }

    if (!this.labReq) {
      const newLabReq: ILabReqDTO = {
        customer: this.customer._id,
        adminUser: this.user._id,
        templateId: this.template.id,
        templateVariant: this.template.variant,
        data: this.labReqBase.value,
        dueDate: this.dueDate,
        v: 2
      };

      this.facade.saveLabReq(newLabReq);
    } else {
      const updatedLabReq: ILabReqDTO = {
        adminUser: this.user._id,
        data: this.labReqBase.value,
        dueDate: this.dueDate
      };

      this.facade.updateLabReq(this.labReq._id, updatedLabReq);
    }
  }

  close(): void {
    this.nzDrawerRef.close(true);
  }
}
